/**
 * Regras puras da lista de empresas: busca, filtros combinaveis com contagem
 * facetada, ordenacao por coluna, paginacao e a consulta na URL.
 *
 * Decisoes nao obvias:
 *  1. Pensada para CENTENAS de empresas: a tela nunca renderiza a lista
 *     inteira. Busca e filtros recortam, a ordem decide quem vem primeiro e a
 *     vista entrega uma pagina (tabela) ou um lote com "ver mais" (cartoes).
 *  2. Contagem FACETADA: o numero ao lado de cada opcao diz quantas empresas
 *     sobrariam se ela fosse escolhida, mantidos os OUTROS filtros e a busca.
 *     Contagem "absoluta" mentiria ("Kiwify 40" e, ao clicar, 3 resultados
 *     porque o status ja estava em Pausadas).
 *  3. A busca ignora acento e caixa e aceita varias palavras (todas precisam
 *     aparecer): "fit kiwify" acha a FitPro pelo nome e pelo provedor.
 *  4. Ordenacao: vazios (sem metrica, sem base anterior) vao SEMPRE para o
 *     fim, nos dois sentidos -- "sem dado" no topo de "mais recuperado" seria
 *     ruido. Empate desempata pelo nome, para a ordem ser estavel.
 *  5. A consulta mora na URL com prefixo `emp.` (`emp.q`, `emp.status`...):
 *     link compartilhavel, e o "voltar" do navegador devolve a lista no mesmo
 *     recorte. O prefixo existe porque a URL viaja entre telas (o periodo mora
 *     nela) e a lista de leads ja usa `q`, `funil` e `ordem` para outra coisa.
 */

import { variar } from './comparar';
import type { CompanySummary } from '../../types';
import type {
  ColunaLista,
  CompanyOverviewItem,
  ConsultaLista,
  FiltroCobranca,
  FiltroFunil,
  FiltroProvedor,
  FiltroSaude,
  FiltroStatus,
  OrdemLista,
  ProvedorIntegracao,
  SaudeDaEmpresa,
  VistaLista,
} from './types';
import { COLUNA_INFO, MODELO_COBRANCA, NIVEL_SAUDE, PESO_SAUDE, PROVEDORES, PROVEDOR_INFO, STATUS_EMPRESA, normalizar } from './vocabulario';

export const TAMANHO_DA_PAGINA = 10;
export const LOTE_DE_CARTOES = 9;
/** Acima disto a vista automatica passa de cartoes para tabela. */
export const LIMITE_PARA_TABELA = 12;

export interface LinhaEmpresa {
  readonly company: CompanySummary;
  readonly item: CompanyOverviewItem | null;
  readonly saude: SaudeDaEmpresa | null;
  /** Conectados de fato (visao geral); sem ela, o que o cadastro declara. */
  readonly provedores: readonly ProvedorIntegracao[];
}

export const CONSULTA_PADRAO: ConsultaLista = {
  busca: '',
  status: 'todas',
  provedor: 'todos',
  funil: 'todos',
  cobranca: 'todos',
  saude: 'todas',
  ordem: { coluna: 'recuperado', desc: true },
  vista: null,
};

export function vistaEfetiva(consulta: ConsultaLista, total: number): VistaLista {
  return consulta.vista ?? (total > LIMITE_PARA_TABELA ? 'tabela' : 'cartoes');
}

// -----------------------------------------------------------------------------
// Filtro
// -----------------------------------------------------------------------------

function textoDaLinha(linha: LinhaEmpresa): string {
  const c = linha.company;
  return normalizar(
    [
      c.name,
      c.segment,
      c.slug,
      c.id,
      c.contactName,
      c.contactEmail,
      STATUS_EMPRESA[c.status].label,
      MODELO_COBRANCA[c.billingModel].curto,
      ...linha.provedores.map((p) => PROVEDOR_INFO[p].curto),
      linha.saude === null ? '' : NIVEL_SAUDE[linha.saude.nivel].label,
    ].join(' '),
  );
}

function passaNaBusca(linha: LinhaEmpresa, busca: string): boolean {
  const termos = normalizar(busca)
    .split(/\s+/)
    .filter((t) => t !== '');
  if (termos.length === 0) {
    return true;
  }
  const texto = textoDaLinha(linha);
  return termos.every((t) => texto.includes(t));
}

export function passa(linha: LinhaEmpresa, consulta: ConsultaLista): boolean {
  const c = linha.company;
  if (consulta.status !== 'todas' && c.status !== consulta.status) {
    return false;
  }
  if (consulta.provedor === 'nenhum' && linha.provedores.length > 0) {
    return false;
  }
  if (consulta.provedor !== 'todos' && consulta.provedor !== 'nenhum' && !linha.provedores.includes(consulta.provedor)) {
    return false;
  }
  if (consulta.funil !== 'todos' && !c.funnels.includes(consulta.funil)) {
    return false;
  }
  if (consulta.cobranca !== 'todos' && c.billingModel !== consulta.cobranca) {
    return false;
  }
  if (consulta.saude !== 'todas' && linha.saude?.nivel !== consulta.saude) {
    return false;
  }
  return passaNaBusca(linha, consulta.busca);
}

export function filtrar(linhas: readonly LinhaEmpresa[], consulta: ConsultaLista): readonly LinhaEmpresa[] {
  return linhas.filter((l) => passa(l, consulta));
}

/** Quantas sobrariam com `patch` aplicado, mantidos os outros filtros e a busca. */
export function contar(linhas: readonly LinhaEmpresa[], consulta: ConsultaLista, patch: Partial<ConsultaLista>): number {
  const alvo: ConsultaLista = { ...consulta, ...patch };
  return linhas.reduce((acc, l) => (passa(l, alvo) ? acc + 1 : acc), 0);
}

export interface FiltroAtivo {
  readonly chave: 'busca' | 'status' | 'provedor' | 'funil' | 'cobranca' | 'saude';
  readonly rotulo: string;
  readonly limpar: Partial<ConsultaLista>;
}

export function filtrosAtivos(consulta: ConsultaLista): readonly FiltroAtivo[] {
  const lista: FiltroAtivo[] = [];
  if (consulta.busca.trim() !== '') {
    lista.push({ chave: 'busca', rotulo: `Busca: «${consulta.busca.trim()}»`, limpar: { busca: '' } });
  }
  if (consulta.status !== 'todas') {
    lista.push({ chave: 'status', rotulo: `Status: ${STATUS_EMPRESA[consulta.status].plural}`, limpar: { status: 'todas' } });
  }
  if (consulta.provedor !== 'todos') {
    lista.push({ chave: 'provedor', rotulo: `Provedor: ${consulta.provedor === 'nenhum' ? 'nenhum conectado' : PROVEDOR_INFO[consulta.provedor].curto}`, limpar: { provedor: 'todos' } });
  }
  if (consulta.funil !== 'todos') {
    lista.push({ chave: 'funil', rotulo: `Funil: ${consulta.funil === 'carrinho_abandonado' ? 'Carrinho abandonado' : 'Expirados'}`, limpar: { funil: 'todos' } });
  }
  if (consulta.cobranca !== 'todos') {
    lista.push({ chave: 'cobranca', rotulo: `Cobrança: ${MODELO_COBRANCA[consulta.cobranca].curto}`, limpar: { cobranca: 'todos' } });
  }
  if (consulta.saude !== 'todas') {
    lista.push({ chave: 'saude', rotulo: `Saúde: ${NIVEL_SAUDE[consulta.saude].label}`, limpar: { saude: 'todas' } });
  }
  return lista;
}

// -----------------------------------------------------------------------------
// Ordenacao
// -----------------------------------------------------------------------------

/** Valor numerico da coluna; `null` = sem dado (vai para o fim). */
function valorDa(linha: LinhaEmpresa, coluna: ColunaLista): number | null {
  const m = linha.item?.metrics ?? null;
  switch (coluna) {
    case 'empresa':
      return null;
    case 'saude':
      return linha.saude === null ? null : PESO_SAUDE[linha.saude.nivel] * 1_000 + (100 - linha.saude.nota);
    case 'recuperado':
      return m === null ? null : m.recoveredAmountCents;
    case 'variacao': {
      if (m === null || linha.item === null || linha.item.previous === null) {
        return null;
      }
      const v = variar(m.recoveredAmountCents, linha.item.previous.recoveredAmountCents);
      if (v.relativaBps !== null) {
        return v.relativaBps;
      }
      return v.direcao === 'alta' ? Number.MAX_SAFE_INTEGER : v.direcao === 'queda' ? Number.MIN_SAFE_INTEGER : null;
    }
    case 'comissao':
      return m === null ? null : m.commissionCents;
    case 'taxa':
      return m === null || m.abandonedCount === 0 ? null : m.recoveryRateBps;
    case 'vendas':
      return m === null ? null : m.recoveredCount;
    case 'criada':
      return new Date(linha.company.createdAt).getTime();
  }
}

const COLLATOR = new Intl.Collator('pt-BR', { sensitivity: 'base' });

export function ordenar(linhas: readonly LinhaEmpresa[], ordem: OrdemLista): readonly LinhaEmpresa[] {
  const sinal = ordem.desc ? -1 : 1;
  return [...linhas].sort((a, b) => {
    if (ordem.coluna === 'empresa') {
      return sinal * COLLATOR.compare(a.company.name, b.company.name);
    }
    const va = valorDa(a, ordem.coluna);
    const vb = valorDa(b, ordem.coluna);
    if (va === null && vb === null) {
      return COLLATOR.compare(a.company.name, b.company.name);
    }
    if (va === null) {
      return 1;
    }
    if (vb === null) {
      return -1;
    }
    if (va !== vb) {
      return sinal * (va < vb ? -1 : 1);
    }
    return COLLATOR.compare(a.company.name, b.company.name);
  });
}

/** Clique no cabecalho: mesma coluna inverte; coluna nova entra no sentido natural dela. */
export function alternarOrdem(atual: OrdemLista, coluna: ColunaLista): OrdemLista {
  if (atual.coluna === coluna) {
    return { coluna, desc: !atual.desc };
  }
  return { coluna, desc: COLUNA_INFO[coluna].descPadrao };
}

// -----------------------------------------------------------------------------
// URL
// -----------------------------------------------------------------------------

const CHAVES = {
  busca: 'emp.q',
  status: 'emp.status',
  provedor: 'emp.provedor',
  funil: 'emp.funil',
  cobranca: 'emp.cobranca',
  saude: 'emp.saude',
  ordem: 'emp.ordem',
  dir: 'emp.dir',
  vista: 'emp.vista',
  /** Aba do detalhe: nao e da lista, mas sai junto quando o link deixa o modulo. */
  aba: 'emp.aba',
} as const;

const STATUS_VALIDOS: readonly FiltroStatus[] = ['todas', 'ativa', 'onboarding', 'pausada'];
const PROVEDORES_VALIDOS: readonly FiltroProvedor[] = ['todos', ...PROVEDORES, 'nenhum'];
const FUNIS_VALIDOS: readonly FiltroFunil[] = ['todos', 'carrinho_abandonado', 'expirados'];
const COBRANCAS_VALIDAS: readonly FiltroCobranca[] = ['todos', 'contrato', 'automatico', 'a_definir'];
const SAUDES_VALIDAS: readonly FiltroSaude[] = ['todas', 'saudavel', 'atencao', 'critica', 'implantacao', 'pausada'];
const COLUNAS_VALIDAS: readonly ColunaLista[] = ['empresa', 'saude', 'recuperado', 'variacao', 'comissao', 'taxa', 'vendas', 'criada'];
const VISTAS_VALIDAS: readonly VistaLista[] = ['tabela', 'cartoes'];

function entre<T extends string>(valor: string | null, lista: readonly T[]): T | null {
  return valor === null ? null : lista.find((v) => v === valor) ?? null;
}

export function lerConsulta(search: string): ConsultaLista {
  const p = new URLSearchParams(search);
  const coluna = entre(p.get(CHAVES.ordem), COLUNAS_VALIDAS) ?? CONSULTA_PADRAO.ordem.coluna;
  const dir = p.get(CHAVES.dir);
  const desc = dir === 'asc' ? false : dir === 'desc' ? true : coluna === CONSULTA_PADRAO.ordem.coluna ? CONSULTA_PADRAO.ordem.desc : COLUNA_INFO[coluna].descPadrao;
  return {
    busca: p.get(CHAVES.busca) ?? '',
    status: entre(p.get(CHAVES.status), STATUS_VALIDOS) ?? 'todas',
    provedor: entre(p.get(CHAVES.provedor), PROVEDORES_VALIDOS) ?? 'todos',
    funil: entre(p.get(CHAVES.funil), FUNIS_VALIDOS) ?? 'todos',
    cobranca: entre(p.get(CHAVES.cobranca), COBRANCAS_VALIDAS) ?? 'todos',
    saude: entre(p.get(CHAVES.saude), SAUDES_VALIDAS) ?? 'todas',
    ordem: { coluna, desc },
    vista: entre(p.get(CHAVES.vista), VISTAS_VALIDAS),
  };
}

/** Escreve SO o que difere do padrao, preservando o resto da URL (periodo do cabecalho). */
export function escreverConsulta(search: string, consulta: ConsultaLista): URLSearchParams {
  const p = new URLSearchParams(search);
  for (const chave of Object.values(CHAVES)) {
    p.delete(chave);
  }
  const busca = consulta.busca;
  if (busca.trim() !== '') {
    p.set(CHAVES.busca, busca);
  }
  if (consulta.status !== 'todas') {
    p.set(CHAVES.status, consulta.status);
  }
  if (consulta.provedor !== 'todos') {
    p.set(CHAVES.provedor, consulta.provedor);
  }
  if (consulta.funil !== 'todos') {
    p.set(CHAVES.funil, consulta.funil);
  }
  if (consulta.cobranca !== 'todos') {
    p.set(CHAVES.cobranca, consulta.cobranca);
  }
  if (consulta.saude !== 'todas') {
    p.set(CHAVES.saude, consulta.saude);
  }
  if (consulta.ordem.coluna !== CONSULTA_PADRAO.ordem.coluna || consulta.ordem.desc !== CONSULTA_PADRAO.ordem.desc) {
    p.set(CHAVES.ordem, consulta.ordem.coluna);
    p.set(CHAVES.dir, consulta.ordem.desc ? 'desc' : 'asc');
  }
  if (consulta.vista !== null) {
    p.set(CHAVES.vista, consulta.vista);
  }
  return p;
}

/** Search da URL SEM a consulta da lista: para links que saem daqui e nao devem carregar o recorte. */
export function semConsulta(search: string): string {
  const p = new URLSearchParams(search);
  for (const chave of Object.values(CHAVES)) {
    p.delete(chave);
  }
  const texto = p.toString();
  return texto === '' ? '' : `?${texto}`;
}
